import useContext from '../../hooks/useContext';
import { SiteContext } from '../../SiteContext';
import { useTrackAlertBanner } from '../../hooks/tracking/useTrackingEvents';
import { Banner, BannersWrapper, Dot, Message, Wrapper } from './wrapper';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from '../../../src/styled/Link';
import Body from '@components/Typography/Body';
import { ChevronRightIcon } from '../../styled/Icon';
export interface IAlertBannerProps {
  data: IAlertBanner;
  condensedDate?: boolean;
}

export interface IAlertBannerContainer {
  alertBanners: IAlertBannerProps[];
}

const resolveCountdown = (alertBanner: IAlertBanner, condensedDate: boolean) => {
  if (!alertBanner?.countdownOptions.endDate) return;

  const countdownDate = new Date(alertBanner.countdownOptions.endDate).getTime();
  let humanReadableDate = '';

  // Get today's date and time
  const now = new Date().getTime();

  // Find the distance between now and the countdown date
  const distance = countdownDate - now;

  // Time calculations for days, hours, minutes and seconds
  const days = Math.floor(distance / (1000 * 60 * 60 * 24));
  const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((distance % (1000 * 60)) / 1000);

  if (condensedDate) {
    humanReadableDate = `${days}:${hours}:${minutes}:${seconds}`;
  } else {
    humanReadableDate = `${days} dagar ${hours} timmar ${minutes} minuter ${seconds} sekunder`;
  }

  if (distance > 0) {
    return humanReadableDate;
  }
};

export const AlertBanner = ({ data, condensedDate }: IAlertBannerProps) => {
  const countdownInterval = useRef<NodeJS.Timeout>();
  const [countdown, setCountdown] = useState<string>();

  const sendClickEvent = useTrackAlertBanner(data?.name);

  useEffect(() => {
    // Reset old countdown
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      setCountdown(undefined);
    }

    // Set new value immediately
    setCountdown(resolveCountdown(data, !!condensedDate));

    // Initiate a new countdown
    if (data && data.countdownOptions.isActive) {
      countdownInterval.current = setInterval(() => {
        const dateString = resolveCountdown(data, !!condensedDate);

        if (dateString) {
          setCountdown(dateString);
        } else {
          // Clear interval and countdown if counter is less than now
          clearInterval(countdownInterval.current);
          setCountdown(undefined);
          return;
        }
      }, 1000);
    }

    return () => clearInterval(countdownInterval.current);
  }, [data]);

  if (!data) {
    clearInterval(countdownInterval.current);
    return null;
  }

  const {
    textMessage,
    urls: { redirectTo },
    backgroundColor,
    textColor,
  } = data;

  return (
    <Banner data-testid="alert-banner" $bg={backgroundColor} $color={textColor}>
      <Link onClick={sendClickEvent} href={redirectTo}>
        <Body $size="sm" $fontWeight="600">
          <Wrapper>
            <Message>
              <div>{textMessage}</div>
              {countdown && <Dot>•</Dot>}
              <div>{countdown}</div>
            </Message>
            {redirectTo && <ChevronRightIcon strokeWidth="2" />}
          </Wrapper>
        </Body>
      </Link>
    </Banner>
  );
};

const AlertBannerWrapper = () => {
  const {
    data: { alertBanners },
  } = useContext(SiteContext);
  if (!alertBanners) {
    return null;
  }

  return (
    <BannersWrapper>
      {alertBanners.map((i) => (
        <AlertBanner key={i.id} data={i} condensedDate={false} />
      ))}
    </BannersWrapper>
  );
};

export default AlertBannerWrapper;
